import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";

export default function PageHero({
  category = "Services",
  categoryLink = "/services",
  title,
  tagline,
  bgImage,
  ctaLabel = "Request Consultation",
  ctaHref = "/#contact",
  theme = "dark",
}) {
  const isLight = theme === "light";

  return (
    <section
      className={`relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28 ${
        isLight ? "bg-[#FAFAFA] border-b border-navy/10" : "bg-navy"
      }`}
    >
      {/* Background with subtle overlay */}
      {bgImage && (
        <div className="absolute inset-0">
          <img
            src={bgImage}
            alt={title}
            className={`h-full w-full object-cover filter contrast-125 ${
              isLight ? "opacity-10" : "opacity-20"
            }`}
          />
          <div
            className={`absolute inset-0 ${
              isLight
                ? "bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA]/90 to-transparent"
                : "bg-gradient-to-t from-navy via-navy/80 to-navy/40"
            }`}
          />
        </div>
      )}

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        {/* Breadcrumb navigation */}
        <nav
          aria-label="Breadcrumb"
          className={`mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
            isLight ? "text-navy/60" : "text-white/60"
          }`}
        >
          <Link to="/" className="transition hover:text-[#FF5722]">
            Home
          </Link>
          <ChevronRight size={14} className={isLight ? "text-navy/40" : "text-white/40"} />
          <Link to={categoryLink} className="transition hover:text-[#FF5722]">
            {category}
          </Link>
          <ChevronRight size={14} className={isLight ? "text-navy/40" : "text-white/40"} />
          <span className="text-[#FF5722]">{title}</span>
        </nav>

        {/* Category Pill */}
        <div
          className={`mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs font-bold uppercase tracking-widest text-[#FF5722] backdrop-blur-sm ${
            isLight ? "border border-[#FF5722]/25 bg-[#FF5722]/5" : "border border-white/15 bg-white/5"
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#FF5722]" />
          <span>{category}</span>
        </div>

        {/* H1 Title */}
        <h1
          className={`max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl ${
            isLight ? "text-navy" : "text-white"
          }`}
        >
          {title}
        </h1>

        {/* Tagline */}
        {tagline && (
          <p
            className={`mt-6 max-w-2xl font-body text-lg leading-relaxed sm:text-xl ${
              isLight ? "text-navy/70" : "text-white/80"
            }`}
          >
            {tagline}
          </p>
        )}

        {/* Action Button */}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <a
            href={ctaHref}
            className="inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#E64A19] shadow-lg shadow-[#FF5722]/20"
          >
            {ctaLabel} <ArrowRight size={16} />
          </a>
          <Link
            to={categoryLink}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider transition ${
              isLight
                ? "border border-navy/20 text-navy hover:border-navy hover:bg-navy/5"
                : "border border-white/20 text-white hover:border-white hover:bg-white/10"
            }`}
          >
            All {category}
          </Link>
        </div>
      </div>
    </section>
  );
}
